import { useEffect, useRef, useState } from 'preact/hooks';
import * as alerts from '../alerts';
import { accept, api, available, chats, connection, dismissChat, end, loadReference, markRead, me, offices, presence, rings, savedReplies, selected, send, setAvailable, stillThere, transfer, typing, type Chat, type Ring } from '../state';

const time = (iso: string) => new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
const officeShort = (o: string | null) => o?.replace(/, [A-Z]{2}$/, '') ?? 'No office';

export function Chats() {
  const [minimized, setMinimized] = useState(false);
  const ringing = rings.value;
  useEffect(() => {
    if (ringing.length) setMinimized(false); // any new ring re-opens the alert
  }, [ringing.length]);
  useEffect(() => void loadReference(), []);

  const list = Object.values(chats.value).sort((a, b) => (a.conversation.acceptedAt ?? '').localeCompare(b.conversation.acceptedAt ?? ''));
  const current = selected.value && !selected.value.startsWith('ring:') ? chats.value[selected.value] : undefined;

  return (
    <div class="chats">
      <aside class="side">
        <AvailabilityCard />
        <section>
          <h3>Your chats</h3>
          {list.length === 0 && <p class="muted small">No chats yet. When a visitor in your area starts one, you'll hear it ring.</p>}
          {list.map((c) => (
            <button class={`chat-item ${selected.value === c.conversation.id ? 'on' : ''} ${c.conversation.state} ${c.waiting ? 'waiting' : ''}`} onClick={() => { selected.value = c.conversation.id; markRead(c.conversation.id); }}>
              <strong>{c.conversation.name}</strong>
              <span>
                {officeShort(c.conversation.office)} · {c.conversation.state !== 'active' ? 'Ended' : c.waiting ? 'Waiting on you' : c.visitorAway ? 'Left the page' : c.visitorTyping ? 'Typing…' : 'Live'}
              </span>
              {c.unread > 0 && <em class="count">{c.unread}</em>}
            </button>
          ))}
        </section>
        <Team />
      </aside>

      <section class="work">
        {current ? <ChatView chat={current} /> : <Empty />}
      </section>

      {ringing.length > 0 && (minimized ? <RingBar rings={ringing} onOpen={() => setMinimized(false)} /> : <RingOverlay rings={ringing} onMinimize={() => setMinimized(true)} />)}
      {stillThere.value && available.value && <StillThere />}
    </div>
  );
}

function AvailabilityCard() {
  const [problem, setProblem] = useState('');
  const [busy, setBusy] = useState(false);
  const on = available.value;
  const online = connection.value === 'online';

  async function goAvailable() {
    setBusy(true);
    setProblem('');
    const sound = await alerts.unlock(true); // inside the click: unlocks audio and plays a test ring
    const notes = await alerts.requestNotifications();
    if (!notes) {
      setProblem('Desktop notifications are blocked. Click the lock icon in the address bar → Notifications → Allow, then try again.');
    } else if (!sound) {
      setProblem('Sound could not be turned on. Check your volume and try again.');
    } else if (!(await setAvailable(true))) {
      setProblem('Could not go available — check your connection.');
    }
    setBusy(false);
  }

  return (
    <div class={`availability ${on ? 'on' : 'off'}`}>
      <div class="state">
        <span class="dot" />
        <div>
          <strong>{on ? 'Available for chats' : 'Not available'}</strong>
          <span>{on ? `Ringing for ${me.value?.offices.map(officeShort).join(', ') || 'no offices'} — and any office when they're busy.` : 'You won’t be rung for new chats.'}</span>
        </div>
      </div>
      {on ? (
        <button
          class="btn"
          onClick={() => {
            const open = Object.values(chats.value).filter((c) => c.conversation.state === 'active').length;
            if (open && !confirm(`You have ${open} open chat${open > 1 ? 's' : ''}. Going unavailable will close ${open > 1 ? 'them' : 'it'} with a thank-you message. Continue?`)) return;
            void setAvailable(false);
          }}
        >
          Go unavailable
        </button>
      ) : (
        <button class="btn primary big" disabled={busy || !online} onClick={goAvailable}>{busy ? 'Checking…' : 'Go available'}</button>
      )}
      {!on && <p class="muted small">You’ll hear a short test ring. Keep this tab open and your volume up.</p>}
      {problem && <p class="error">{problem}</p>}
      {!online && <p class="error">Reconnecting to the server…</p>}
    </div>
  );
}

function Countdown({ until, total = 60_000 }: { until: number; total?: number }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(t);
  }, []);
  const left = Math.max(0, Math.ceil((until - now) / 1000));
  const pct = Math.min(100, ((until - now) / total) * 100);
  return (
    <span class="countdown" style={{ '--pct': `${pct}%` } as Record<string, string>}>
      <span>{left}s</span>
    </span>
  );
}

function RingOverlay({ rings, onMinimize }: { rings: Ring[]; onMinimize: () => void }) {
  return (
    <div class="ring-overlay" role="alertdialog" aria-live="assertive" aria-label="Incoming chat">
      <div class="ring-panel">
        <p class="ring-kicker">Incoming chat{rings.length > 1 ? `s (${rings.length})` : ''}</p>
        {rings.map((r) => (
          <div class="ring-card">
            <Countdown until={r.expiresAt} total={r.durationMs} />
            <div class="ring-info">
              <h2>{officeShort(r.conversation.office)}</h2>
              <p><strong>{r.conversation.name}</strong> · ZIP {r.conversation.zip}</p>
              {r.conversation.preview ? <blockquote class="preview">“{r.conversation.preview}”</blockquote> : <p class="muted small">Typing their question…</p>}
              <p class="muted small">{r.stage === 'office' ? 'Your office' : 'All available agents are being rung'}</p>
            </div>
            <button class="btn accept" onClick={() => accept(r.conversation.id)}>Accept</button>
          </div>
        ))}
        <button class="link" onClick={onMinimize}>Minimize (keeps ringing)</button>
      </div>
    </div>
  );
}

function RingBar({ rings, onOpen }: { rings: Ring[]; onOpen: () => void }) {
  const r = rings[0];
  return (
    <div class="ring-bar" role="alert">
      <span>🔔 {rings.length} chat{rings.length > 1 ? 's' : ''} waiting — {r.conversation.name}, {officeShort(r.conversation.office)}</span>
      <Countdown until={r.expiresAt} total={r.durationMs} />
      <button class="btn accept small" onClick={() => accept(r.conversation.id)}>Accept</button>
      <button class="link-light" onClick={onOpen}>Show</button>
    </div>
  );
}

function StillThere() {
  const [left, setLeft] = useState(60);
  useEffect(() => {
    const t = setInterval(() => setLeft((s) => s - 1), 1000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => {
    if (left <= 0) void setAvailable(false);
  }, [left]);
  return (
    <div class="ring-overlay soft" role="alertdialog" aria-label="Are you still available?">
      <div class="ring-panel">
        <h2>Still available?</h2>
        <p>A chat rang out without an answer. If you’re away from your desk, we’ll set you unavailable in <strong>{Math.max(left, 0)}s</strong> so visitors aren’t kept waiting.</p>
        <div class="row">
          <button class="btn primary" onClick={() => setAvailable(true)}>Yes, I’m here</button>
          <button class="btn" onClick={() => setAvailable(false)}>Go unavailable</button>
        </div>
      </div>
    </div>
  );
}

function Team() {
  const list = presence.value.filter((p) => p.id !== me.value?.id);
  return (
    <section>
      <h3>Team online</h3>
      {list.length === 0 && <p class="muted small">Nobody else is signed in.</p>}
      {list.map((p) => (
        <p class="presence">
          <span class={`dot ${p.available ? 'on' : ''}`} />
          {p.name} <span class="muted small">{p.offices.map(officeShort).join(', ')}</span>
        </p>
      ))}
    </section>
  );
}

function Empty() {
  return (
    <div class="empty">
      <img src="/icon-192.png" alt="" width={72} height={72} />
      <h2>{available.value ? 'Waiting for chats' : 'Go available to take chats'}</h2>
      <p class="muted">New chats ring here with sound, a desktop notification, and a flashing tab — even when this tab is in the background.</p>
      <p class="muted small">Tip: install this as an app (⋮ menu → Install Guardian Chat) for its own window and a badge on your dock or taskbar.</p>
    </div>
  );
}

type Activity = { id: number; type: 'form' | 'chat' | 'call' | 'sms'; office: string | null; occurredAt: string; summary: Record<string, any> };

function describe(a: Activity) {
  const s = a.summary;
  if (a.type === 'form') return s.service || s.message?.slice(0, 60) || 'Website form';
  if (a.type === 'chat') return { answered: `Chat with ${s.agent ?? 'agent'}`, missed: 'Missed chat', offline: 'Chat while offline', abandoned: 'Left before an answer' }[s.outcome as string] ?? 'Chat';
  return a.type === 'call' ? 'Phone call' : 'Text';
}

/** "Returning contact" strip: earlier forms/chats/calls from the same email or phone. */
function History({ id }: { id: string }) {
  const [rows, setRows] = useState<Activity[] | null>(null);
  useEffect(() => {
    setRows(null);
    api<Activity[]>(`/conversations/${id}/history`).then(setRows).catch(() => setRows([]));
  }, [id]);
  if (!rows?.length) return null;
  return (
    <div class="history">
      <strong>Returning contact</strong>
      {rows.slice(0, 3).map((a) => (
        <span class={`pill ${a.type}`} title={a.office ?? ''}>
          {new Date(a.occurredAt).toLocaleDateString([], { month: 'short', day: 'numeric' })} · {describe(a)}
        </span>
      ))}
      {rows.length > 3 && <span class="muted small">+{rows.length - 3} more</span>}
    </div>
  );
}

function TransferMenu({ chat, onClose }: { chat: Chat; onClose: () => void }) {
  const others = offices.value.filter((o) => o !== chat.conversation.office);
  const pick = async (office: string | null) => {
    onClose();
    if (confirm(`Transfer ${chat.conversation.name} to ${office ?? 'anyone available'}? It will ring there like a new chat.`)) await transfer(chat.conversation.id, office);
  };
  return (
    <div class="menu" role="menu">
      <p class="muted small">Transfer to…</p>
      {others.map((o) => <button type="button" role="menuitem" onClick={() => pick(o)}>{o}</button>)}
      <button type="button" role="menuitem" onClick={() => pick(null)}>Anyone available</button>
    </div>
  );
}

function SavedReplies({ filter, onPick, onClose }: { filter: string; onPick: (body: string) => void; onClose: () => void }) {
  const q = filter.toLowerCase();
  const list = savedReplies.value.filter((r) => !q || r.title.toLowerCase().includes(q) || r.body.toLowerCase().includes(q));
  return (
    <div class="menu replies" role="menu">
      <div class="menu-head"><span class="muted small">Saved replies{q ? ` matching “${filter}”` : ''}</span><button type="button" class="link" onClick={onClose}>Close</button></div>
      {list.map((r) => (
        <button type="button" role="menuitem" onClick={() => onPick(r.body)}>
          <strong>{r.title}</strong>
          <span>{r.body}</span>
        </button>
      ))}
      {!list.length && <p class="muted small">No saved replies match.</p>}
    </div>
  );
}

function ChatView({ chat }: { chat: Chat }) {
  const c = chat.conversation;
  const log = useRef<HTMLDivElement>(null);
  const box = useRef<HTMLTextAreaElement>(null);
  const [draft, setDraft] = useState('');
  const [menu, setMenu] = useState<'none' | 'transfer' | 'replies'>('none');
  const live = c.state === 'active';
  const first = c.name.trim().split(/\s+/)[0];
  const slash = draft.startsWith('/') ? draft.slice(1) : null;

  useEffect(() => {
    log.current?.scrollTo({ top: log.current.scrollHeight });
    markRead(c.id);
  }, [chat.messages.length, chat.visitorTyping, chat.waiting, c.id]); // the waiting bar shrinks the log
  useEffect(() => {
    setDraft('');
    setMenu('none');
  }, [c.id]);

  async function submit(e: Event) {
    e.preventDefault();
    const body = draft.trim();
    if (!body || slash !== null) return;
    setDraft('');
    typing(c.id, false);
    await send(c.id, body);
  }

  function useReply(body: string) {
    setDraft(body.replaceAll('{first_name}', first));
    setMenu('none');
    box.current?.focus();
  }

  return (
    <div class="chat">
      <header class="chat-head">
        <div>
          <h2>{c.name}{chat.visitorAway && live && <span class="pill missed">Left the page</span>}</h2>
          <p class="muted small">
            {officeShort(c.office)} · ZIP {c.zip}
            {c.email && <> · <a href={`mailto:${c.email}`}>{c.email}</a></>}
            {c.phone && <> · <a href={`tel:${c.phone.replace(/[^\d+]/g, '')}`}>{c.phone}</a></>}
            {c.pageUrl && <> · from <span title={c.pageUrl}>{new URL(c.pageUrl).pathname}</span></>}
          </p>
          <History id={c.id} />
        </div>
        {live ? (
          <div class="head-actions">
            <button class="btn" onClick={() => setMenu(menu === 'transfer' ? 'none' : 'transfer')}>Transfer</button>
            <button class="btn" onClick={() => confirm('End this chat? The transcript will be emailed to the office.') && end(c.id)}>End chat</button>
            {menu === 'transfer' && <TransferMenu chat={chat} onClose={() => setMenu('none')} />}
          </div>
        ) : (
          <button class="btn" onClick={() => dismissChat(c.id)}>Close</button>
        )}
      </header>
      {chat.waiting && live && <div class="waiting-bar">⏳ {first} is waiting for your reply</div>}
      <div class="log" ref={log}>
        {chat.messages.map((m) => (
          <div class={`msg ${m.sender}`}>
            {m.sender !== 'system' && <span class="who">{m.sender === 'visitor' ? c.name : m.sender === 'bot' ? 'Automatic message' : m.agentName ?? 'You'} · {time(m.createdAt)}</span>}
            <p>{m.body}</p>
          </div>
        ))}
        {chat.visitorTyping && live && <p class="typing">{first} is typing…</p>}
      </div>
      {live ? (
        <form class="composer" onSubmit={submit}>
          {(menu === 'replies' || slash !== null) && <SavedReplies filter={slash ?? ''} onPick={useReply} onClose={() => { setMenu('none'); if (slash !== null) setDraft(''); }} />}
          <button type="button" class="btn icon" title="Saved replies (or type /)" aria-label="Saved replies" onClick={() => setMenu(menu === 'replies' ? 'none' : 'replies')}>⚡</button>
          <textarea
            ref={box}
            value={draft}
            maxLength={2000}
            rows={2}
            placeholder="Type a reply — Enter to send, Shift+Enter for a new line, / for saved replies"
            onInput={(e) => {
              const v = (e.target as HTMLTextAreaElement).value;
              setDraft(v);
              typing(c.id, v.trim().length > 0 && !v.startsWith('/'));
            }}
            onKeyDown={(e) => {
              if (e.key === 'Escape') setMenu('none');
              if (e.key === 'Enter' && !e.shiftKey) submit(e);
            }}
          />
          <button class="btn primary">Send</button>
        </form>
      ) : (
        <p class="ended muted">This chat has ended. The transcript was emailed to the office.</p>
      )}
    </div>
  );
}
